import { ProductInstance, TicketOptionWithQuantity, TicketType } from '@repo/types';
import {
    useReducer,
    createContext,
    PropsWithChildren,
    useContext,
    useRef,
    useEffect,
    useCallback,
    RefObject,
} from 'react';
import {
    bookingReducer,
    initialState,
    BookingState,
    BookingActions,
    PackageOrSingleQuantities,
} from 'src/state/booking/bookingReducer';
import { useBookingEffects } from 'src/state/booking/booking.effects';
import { useAtom } from 'ximple';
import { toggleBookingAtom } from 'src/state/ui/toggleBooking.atom';
import { useFocusTrap } from 'src/hooks/common/useFocusTrap';
import { TimeSlotType } from '@repo/widget-utils/TimeSlotType';
import { useConfigurations } from '@repo/widget-utils/widgetsConfiguration';

type BookingContextState<T extends PackageOrSingleQuantities = TicketOptionWithQuantity> =
    BookingState<T> & {
        bookingDispatch: React.Dispatch<BookingActions<T>>;
        onToggleVisible: (visible: boolean, interaction: 'keyboard' | 'mouse') => void;
        visible: boolean;
        boxRef: RefObject<HTMLDivElement | null>;
        shouldShowBasketOnBook: boolean;
    };

const BookingContext = createContext<BookingContextState<any> | undefined>(undefined);

export type BookingContextProps<T extends PackageOrSingleQuantities> = PropsWithChildren<{
    initialTicketOptions?: T[];
    initialProducts?: ProductInstance[];
    initialTimeSlot?: TimeSlotType;
    initialTicketType?: TicketType;
    isBookingWidget?: boolean;
}>;

export function BookingContextProvider<
    T extends PackageOrSingleQuantities = TicketOptionWithQuantity,
>(props: BookingContextProps<T>) {
    const [
        {
            quantities,
            defaultTicketOptions,
            selectedProducts,
            selectedTimeSlot,
            selectedTicketType,
            selectedProductOption,
            newCartItem,
            isLoadingPrices,
            cartItems,
            attemptedBooking,
            hasChosenDate,
            hasChangedQuantities,
        },
        bookingDispatch,
    ] = useReducer(
        bookingReducer,
        initialState<T>({
            quantities: props.initialTicketOptions || [],
            selectedProducts: props.initialProducts,
            selectedTimeSlot: props.initialTimeSlot,
            selectedTicketType: props.initialTicketType,
        }),
    );

    useBookingEffects<T>({ newCartItem, bookingDispatch });

    const [{ visible, interaction }] = useAtom(toggleBookingAtom);

    const config = useConfigurations();
    const shouldShowBasketOnBook = !config.skipBasketOnBook;

    // @TODO: Rename boxRef to bookingCardRef?
    const boxRef = useRef<HTMLDivElement | null>(null);
    const refocusElement = document
        .querySelector('bilberry-booking-button')
        ?.shadowRoot?.querySelector('button') as HTMLElement | null;
    const trap = useFocusTrap(
        boxRef,
        !!props.isBookingWidget && visible && interaction === 'keyboard',
        { current: refocusElement },
    );

    useEffect(() => {
        if (trap && !visible && props.isBookingWidget) trap.deactivate();
    }, [trap, visible, props.isBookingWidget]);

    // Move focus into widget when it opens (for both keyboard and mouse interactions)
    useEffect(() => {
        if (props.isBookingWidget && visible && boxRef.current) {
            const focusableSelectors =
                'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';
            const focusableElements = boxRef.current.querySelectorAll(
                focusableSelectors,
            ) as NodeListOf<HTMLElement>;

            if (focusableElements.length > 0) {
                const timeoutId = setTimeout(() => {
                    focusableElements[0]?.focus();
                }, 100);

                return () => clearTimeout(timeoutId);
            }
        }
    }, [visible, props.isBookingWidget]);

    const onToggleVisible = useCallback(
        (visible: boolean, interaction: 'keyboard' | 'mouse') => {
            if (props.isBookingWidget) toggleBookingAtom.update({ visible, interaction });
        },
        [props.isBookingWidget],
    );

    return (
        <>
            <BookingContext.Provider
                value={{
                    cartItems,
                    newCartItem,
                    quantities,
                    defaultTicketOptions,
                    selectedProducts,
                    selectedTimeSlot,
                    selectedTicketType,
                    selectedProductOption,
                    isLoadingPrices,
                    attemptedBooking,
                    hasChosenDate,
                    onToggleVisible,
                    visible: props.isBookingWidget ? visible : true,
                    boxRef,
                    shouldShowBasketOnBook,
                    hasChangedQuantities,
                    bookingDispatch,
                }}
            >
                {props.children}
            </BookingContext.Provider>
        </>
    );
}

export function useBookingContext<
    T extends PackageOrSingleQuantities = TicketOptionWithQuantity,
>(): BookingContextState<T> {
    const context = useContext(BookingContext);
    if (context === undefined) {
        throw new Error('useBookingContext must be used within a BookingContextProvider');
    }
    return context as BookingContextState<T>;
}
